"use client";

import { ConfigurationCard } from "@/components/configuration/card";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/rac/carousel";
import { ArrowRight } from "lucide-react";
import { useState } from "react";

export interface Configuration {
	id: string;
	type: string; // bhktype - studio/2bhk etc
	price: string;
	bedrooms: number;
	bathrooms: number;
	facing: string;
	builtupArea?: string;
	image: string;
	plotArea?: string;
	basePrice?: string;
	projectName: string;
	locality: string;
	projectId: string;
}

interface ProjectConfigurationsProps {
	configurations: Configuration[];
}

export function ProjectConfigurations({ configurations }: ProjectConfigurationsProps) {
	// Initialize all hooks first to avoid conditional hook calls
	const [api, setApi] = useState<CarouselApi>();
	const [selectedType, setSelectedType] = useState<string>("");

	// Add null safety check after hooks
	if (!configurations || configurations.length === 0) return null;

	const configTypes = Object.keys(
		Object.fromEntries(
			Object.entries(
				configurations.reduce((acc: { [key: string]: boolean }, config) => {
					acc[config.type] = true;
					return acc;
				}, {}),
			).filter(([_, value]) => value),
		),
	);

	// Set the selected type if not already set
	if (!selectedType && configTypes.length > 0) {
		setSelectedType(configTypes[0]);
	}

	const filteredConfigs = configurations.filter(config => config.type === selectedType);

	return (
		<>
			{/* Configuration Cards Carousel */}
			<div className="pb-6">
				<Carousel opts={{ align: "center", loop: true }} className="w-full" setApi={setApi}>
					<div className="lg:flex lg:justify-between">
						{/* Mobile Tabs */}
						<div className="flex gap-2 mb-2 lg:mb-8 overflow-x-auto">
							{configTypes.map(type => (
								<button
									type="button"
									key={type}
									onClick={() => {
										setSelectedType(type);
									}}
									className={`cursor-pointer py-2 px-4 text-sm font-bold font-ruda border-b-2 transition-colors whitespace-nowrap
							${selectedType === type ? "border-red-500 text-red-500 bg-white" : "border-transparent text-gray-600 bg-gray-50"}
						`}
									style={{ minWidth: 100 }}
								>
									{type}
								</button>
							))}
						</div>
						{/* Show carousel buttons only when scrolling is possible */}
						{api?.canScrollNext() && (
							<div className="flex gap-2 justify-end mb-2 self-end lg:self-auto lg:justify-end">
								<button
									type="button"
									onClick={() => api?.scrollPrev()}
									disabled={!api?.canScrollPrev()}
									className="cursor-pointer static text-black border-gray-300 h-10 w-20 rounded-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200 border"
									aria-label="Previous project"
								>
									<ArrowRight className="w-4 h-4 rotate-180" />
								</button>
								<button
									type="button"
									onClick={() => api?.scrollNext()}
									disabled={!api?.canScrollNext()}
									className="cursor-pointer static text-black border-gray-300 h-10 w-20 rounded-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200 border"
									aria-label="Next project"
								>
									<ArrowRight className="w-4 h-4" />
								</button>
							</div>
						)}
					</div>
					<CarouselContent>
						{filteredConfigs.map(config => (
							<CarouselItem key={config.id} className="flex justify-center md:flex-[0_0_50%]! lg:flex-[0_0_33%]!">
								<div className="w-full m-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] rounded-2xl overflow-hidden">
									<ConfigurationCard config={config} isActive={true} />
								</div>
							</CarouselItem>
						))}
					</CarouselContent>
				</Carousel>
			</div>
		</>
	);
}
